import { Combobox, ComboboxItem, ComboboxList, ComboboxProvider, Select, SelectItem, SelectPopover, SelectProvider } from '@ariakit/react';
import { isFunction, isStringEmpty } from '@mechhive/helpers';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { useSearch } from '~/hooks/useSearch';
import { useTranslate } from '~/hooks/useTranslate';
import { Icon, IconName } from '../ui/Icon';
import clsx from 'clsx';

type InputSelectOption = {
  label?: string;
  value: string;
  variant?: 'normal' | 'invisible';
}

type InputSelectProps = {
  className?: string;
  name?: string;
  title?: string;
  subTitle?: string;
  options?: Array<InputSelectOption>;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  sortValues?: boolean;
  onChange?: ( value ) => void;
  onBlur?: () => void;
  onClickItem?: ( option: InputSelectOption ) => void;
}

export const InputSelect = ( { className, value, name, title, subTitle, options = [], onChange, onBlur, required, disabled, placeholder, sortValues = true, onClickItem } : InputSelectProps ) => {
  const t = useTranslate();
  const [ searchQuery, setSearchQuery ] = useState<string>( '' );
  const { search, suggestions } = useSearch( options, { keys: ['value', 'label' ] } );
  const [ open, setOpen ] = useState<boolean>();
  const buttonRef = useRef<HTMLButtonElement>( null );

  const selectedOption = useMemo( () => {
    return options?.find( o => o.value === value );
  }, [ value, options ] );

  const listOptions = useMemo( () => {
    const listOptions = (
      searchQuery.length > 0 && suggestions.length > 0 ? suggestions : options
    );

    if ( sortValues === true && suggestions.length === 0 ) {
      return listOptions.sort( ( a, b ) => {
        return ( a.label ?? a.value ) > ( b.label ?? b.value ) ? 1 : -1;
      } );
    }

    return listOptions;
  }, [ options, suggestions ] );

  return (
    <ComboboxProvider
      setValue={ ( value ) => {
        startTransition( () => {
          search( value );
          setSearchQuery( value );
        } );
      } }
      resetValueOnHide={ true }
    >
      <SelectProvider
        placement='bottom'
        value={ value }
        open={ open }
        setOpen={ setOpen }
        setValue={ ( value ) => {
          if ( isFunction( onChange ) ) {
            onChange( value );
          }
        } }
      >
        <div className={ 'w-full flex flex-col gap-3' }>
          { ( title || subTitle ) &&
            <div className={ 'flex flex-col gap-1.5' }>
              { title &&
                <div className={ 'text-[#ABB0C7] text-left' }>
                  { title }
                </div>
              }
              { subTitle && 
                <div className={ 'text-[#ABB0C7] text-sm text-left' }>
                  { subTitle }
                </div>
              }
            </div>
          }
          <Select
            ref={ buttonRef }
            name={ name }
            required={ required }
            disabled={ disabled }
            className={ 'group w-full p-4 bg-[#0C102E]/50 border border-[#7C7AFE] focus:border-[#5A7DFB] active:border-[#5A7DFB] outline-none rounded-[10px] leading-5 text-base shadow-[0px_4px_10px_0px_rgba(90,125,251,0.0),0px_-8px_20px_0px_rgba(255,255,255,0.20)_inset] min-h-[54px] focus:shadow-[0px_4px_10px_0px_rgba(90,125,251,0.40),0px_-8px_20px_0px_rgba(90,125,251,0.20)_inset]' }
            onBlur={ onBlur }
            type='button'
          >
            <div className={ 'relative' }>
              <p className={ 'text-left' }>
                { isStringEmpty( value ) &&
                  <span>{ placeholder }</span>  
                }
                { !isStringEmpty( value ) &&
                  <span className={ 'text-white' }>{ selectedOption?.label ?? selectedOption?.value ?? value }</span>
                }
              </p>
              <div className={ 'absolute inset-y-0 pr-0 right-0 flex items-center justify-center text-[#ABB0C7] group-disabled:opacity-20' }>
                <Icon 
                  name={ IconName.ChevronSelectorVertical }
                />
              </div>
            </div>
          </Select>
        </div>
        <SelectPopover 
          className={ clsx(
            'bg-[#0B102F] border border-[#5A7DFB] my-2.5 rounded-[10px] backdrop-blur-md overflow-hidden z-[1000]',
            {
              'pt-4': options.length > 5
            }
          ) }
          sameWidth
          portal={ true }
        >
          { options.length > 5 &&
            <div className={ 'sticky top-0 px-2 w-full bg-inherit' }>
              <div className={ 'absolute inset-y-0 left-0 pl-4' }>
                <Icon
                  name={ IconName.SearchAlt_2 }
                  className={ 'icon-size-5' } />
              </div>
              <Combobox 
                autoSelect={ true }
                className={ 'bg-transparent w-full border-b border-[#4D516E] pb-3 outline-none px-2 pl-10' }
                placeholder={ t( 'INPUT_SEARCH_PLACEHOLDER' ) }
              />
            </div>
          }
          <div className={ 'overflow-auto' }>
            <ComboboxList
              className={ 'max-h-48' }> 
              { listOptions.map( ( option, index ) => {
                const variant = option.variant ?? 'normal';

                return (
                  <SelectItem
                    key={ option.value }
                    value={ option.value }
                    className={ clsx(
                      'px-4 py-3.5 hover:bg-white/10 cursor-pointer data-[active-item]:bg-white/20',
                      {
                        'text-[#74788B]': variant === 'invisible'
                      }
                    ) }
                    onClick={ () =>{
                      if ( isFunction( onClickItem ) ) {
                        onClickItem( option );
                      }
                    } }
                    render={ 
                      <ComboboxItem>
                        { option.label ?? option.value }
                      </ComboboxItem> 
                    }
                  />
                )
              } ) }
            </ComboboxList>
          </div>
        </SelectPopover>
      </SelectProvider>
    </ComboboxProvider>
  )
}